import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Bug, Trash2, Copy, X, ChevronDown } from 'lucide-react';
import { useDebugLogs, clearLogs, getLogs } from '../../lib/debugLog';
import { DebugLogEntryRow } from './DebugLogEntry';
import { useAuth } from '../../hooks/useAuth';

export const DebugPanel: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const [copied, setCopied] = useState(false);
  const logs = useDebugLogs();
  const scrollRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();

  // Visível apenas para superadmins.
  const isEnabled = user?.is_superadmin === true;

  // Atalho desktop: Ctrl+Shift+D
  // Trigger externo (sidebar, etc.): window.dispatchEvent(new Event('vf:toggle-debug-panel'))
  useEffect(() => {
    if (!isEnabled) return;
    const keyHandler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };
    const eventHandler = () => setIsOpen((prev) => !prev);
    document.addEventListener('keydown', keyHandler);
    window.addEventListener('vf:toggle-debug-panel', eventHandler);
    return () => {
      document.removeEventListener('keydown', keyHandler);
      window.removeEventListener('vf:toggle-debug-panel', eventHandler);
    };
  }, [isEnabled]);

  // Auto-scroll ao receber novos logs
  useEffect(() => {
    if (autoScroll && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs.length, autoScroll]);

  // Detectar scroll manual para desabilitar auto-scroll
  const handleScroll = useCallback(() => {
    if (!scrollRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
    const isAtBottom = scrollHeight - scrollTop - clientHeight < 40;
    setAutoScroll(isAtBottom);
  }, []);

  const handleCopy = useCallback(() => {
    const text = getLogs()
      .map(
        (l) =>
          `${l.timestamp.toISOString()} [${l.level.toUpperCase()}] [${l.category}] ${l.message}${l.data ? ' ' + JSON.stringify(l.data) : ''}`,
      )
      .join('\n');
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, []);

  const handleClear = useCallback(() => {
    clearLogs();
  }, []);

  if (!isEnabled) return null;

  const errorCount = logs.filter((l) => l.level === 'error').length;
  const warnCount = logs.filter((l) => l.level === 'warn').length;

  const content = (
    <>
      {/* Painel — aberto via Ctrl+Shift+D ou window event 'vf:toggle-debug-panel' (Sidebar) */}
      {isOpen && (
        <div className="fixed bottom-4 right-4 z-[9999] w-[600px] max-w-[calc(100vw-2rem)] h-[420px] max-h-[calc(100vh-2rem)] flex flex-col rounded-xl shadow-2xl overflow-hidden border border-neutral-700/50 bg-neutral-900/95 backdrop-blur-xl">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-neutral-700/50 bg-neutral-900/80 shrink-0">
            <Bug className="w-4 h-4 text-primary-400" />
            <span className="text-sm font-semibold text-white">Auth Debug</span>
            <span className="text-[10px] text-neutral-500 ml-1">
              {logs.length} log{logs.length !== 1 ? 's' : ''}
            </span>

            {/* Contadores */}
            {errorCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-500/20 text-red-400 font-medium">
                {errorCount} err
              </span>
            )}
            {warnCount > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 font-medium">
                {warnCount} warn
              </span>
            )}

            <div className="ml-auto flex items-center gap-1">
              <button
                onClick={handleCopy}
                className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
                title="Copiar logs"
              >
                {copied ? (
                  <span className="text-[10px] text-green-400 font-medium px-1">Copiado!</span>
                ) : (
                  <Copy className="w-3.5 h-3.5" />
                )}
              </button>
              <button
                onClick={handleClear}
                className="p-1.5 rounded-lg text-neutral-400 hover:text-amber-400 hover:bg-neutral-700/50 transition-colors"
                title="Limpar logs"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1.5 rounded-lg text-neutral-400 hover:text-white hover:bg-neutral-700/50 transition-colors"
                title="Fechar (Ctrl+Shift+D)"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>

          {/* Lista de logs */}
          <div
            ref={scrollRef}
            onScroll={handleScroll}
            className="flex-1 overflow-y-auto overflow-x-hidden bg-neutral-950/50"
          >
            {logs.length === 0 ? (
              <div className="flex items-center justify-center h-full text-neutral-600 text-xs">
                Nenhum log ainda. Interaja com o auth flow.
              </div>
            ) : (
              logs.map((entry) => <DebugLogEntryRow key={entry.id} entry={entry} />)
            )}
            <div ref={bottomRef} />
          </div>

          {/* Scroll-to-bottom */}
          {!autoScroll && (
            <button
              onClick={() => {
                setAutoScroll(true);
                bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="absolute bottom-14 right-6 p-1.5 rounded-full bg-primary-600 text-white shadow-lg hover:bg-primary-700 transition-colors"
            >
              <ChevronDown className="w-4 h-4" />
            </button>
          )}

          {/* Footer hint */}
          <div className="px-4 py-1.5 border-t border-neutral-800/50 bg-neutral-900/80 text-[10px] text-neutral-600 shrink-0">
            Ctrl+Shift+D para toggle &middot; Logs ficam em memória apenas
          </div>
        </div>
      )}
    </>
  );

  return createPortal(content, document.body);
};
